// Tool state, serialized as a query string and kept in localStorage only.
// The page URL is never read or written. Works without storage (defaults).

/**
 * Schema: { key: { param: 'l', type: 'number'|'int'|'string'|'bool', default, min, max, values: [] } }
 * Values outside the schema constraints fall back to the default.
 */
export function decode(schema, search) {
  const params = new URLSearchParams(search || '');
  const state = {};
  for (const [key, def] of Object.entries(schema)) {
    const raw = params.get(def.param ?? key);
    state[key] = raw === null ? def.default : coerce(def, raw);
  }
  return state;
}

export function coerce(def, raw) {
  switch (def.type) {
    case 'number':
    case 'int': {
      let n = Number(String(raw).replace(',', '.'));
      if (!Number.isFinite(n)) return def.default;
      if (def.type === 'int') n = Math.round(n);
      if (def.min !== undefined && n < def.min) return def.default;
      if (def.max !== undefined && n > def.max) return def.default;
      return n;
    }
    case 'bool':
      return raw === '1' || raw === 'true';
    default: {
      const s = String(raw);
      if (def.values && !def.values.includes(s)) return def.default;
      return s;
    }
  }
}

/** Encode only values that differ from the default. */
export function encode(schema, state) {
  const params = new URLSearchParams();
  for (const [key, def] of Object.entries(schema)) {
    const v = state[key];
    if (v === undefined || v === null || v === '' || (typeof v === 'number' && !Number.isFinite(v))) continue;
    if (v === def.default) continue;
    params.set(def.param ?? key, def.type === 'bool' ? (v ? '1' : '0') : String(v));
  }
  return params.toString();
}

/** Page address without query or hash, for the copied plain-text result. */
export function pageUrl() {
  try {
    return window.location.origin + window.location.pathname;
  } catch (e) {
    return '';
  }
}

const PREFIX = 'shi-tool:';

export function loadStored(tool) {
  try {
    const raw = window.localStorage.getItem(PREFIX + tool);
    return raw === null ? null : raw;
  } catch (e) {
    return null;
  }
}

export function saveStored(tool, query) {
  try {
    if (query) window.localStorage.setItem(PREFIX + tool, query);
    else window.localStorage.removeItem(PREFIX + tool);
  } catch (e) {
    /* storage unavailable – fine */
  }
}

/** Initial state: the remembered inputs, or '' for defaults. */
export function initialQuery(tool) {
  return loadStored(tool) || '';
}

/** Carry values into another tool's remembered state, e.g. room dimensions before following a link. */
export function handOff(tool, values) {
  const params = new URLSearchParams(loadStored(tool) || '');
  for (const [k, v] of Object.entries(values)) params.set(k, String(v));
  saveStored(tool, params.toString());
}
